var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { WarningAmber as WarningAmberIcon, StarRate as StarRateIcon } from "@mui/icons-material";
import { Box, Grid, Typography, useMediaQuery } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid, GridOverlay, } from "@mui/x-data-grid";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTrials } from "../../utils/api/trials/dataAccess";
import LoadingBackdrop from "../LoadingBackdrop";
import "./trialGrid.css";
// const initialTrial: TrialModel = {
//   id: "000000000-0000-0000-0000-000000000000",
//   name: "Example Trial",
//   trialCode: null,
//   siteName: null,
//   latitude: null,
//   longitude: null,
//   url: null,
//   trialYear: null,
//   startDate: null,
//   endDate: null,
//   trialWarning: null,
//   trialDocument: null,
//   regionName: null,
//   speciesName: null,
//   annualRainfall: null,
//   soilTypeName: null,
//   soilTypeDescription: null,
//   varieties: "",
//   irrigated: false,
//   trialVarieties: [],
//   highlightedTrial: false,
//   siteId: null,
//   speciesId: null,
// };
const trialColumns = [
    {
        field: "name",
        headerName: "Trial",
        minWidth: 175,
        width: 185,
        renderCell: (params) => (React.createElement("div", { style: {
                display: "flex",
                alignItems: "center",
            } },
            params.row.highlightedTrial && (React.createElement(Tooltip, { title: "Highlighted Trial" },
                React.createElement(StarRateIcon, { style: {
                        marginRight: 4,
                        color: "#388E3C",
                        fontSize: "1.2em"
                    } }))),
            params.row.trialWarning && params.row.trialWarning !== "NULL" ? (React.createElement(Tooltip, { title: params.row.trialWarning },
                React.createElement(WarningAmberIcon, { style: {
                        marginRight: 4,
                        color: params.row.trialWarning !== "NULL" ? "#ff3300" : "inherit",
                    } }))) : null,
            params.value)),
    },
    { field: "speciesName", headerName: "Species", minWidth: 115, width: 125 },
    { field: "regionName", headerName: "Region", minWidth: 130, width: 170 },
    {
        field: "trialYear",
        headerName: "Trial Year",
        minWidth: 110,
        width: 110,
        filterable: false,
    },
    {
        field: "annualRainfall",
        headerName: "Avg Annual Rainfall (10yr) ",
        minWidth: 170,
        cellClassName: "center-text",
        headerClassName: "header-text",
    },
    {
        field: "soilTypeName",
        headerName: "Soil Type",
        minWidth: 120,
        renderCell: (params) => (React.createElement(Tooltip, { title: params.row.soilTypeDescription || "Soil Type Description" },
            React.createElement("span", null, params.value))),
    },
    {
        field: "irrigated",
        headerName: "Irrigated",
        //display the value as a tick or cross
        valueFormatter: (params) => (params ? "✔" : "✘"),
    },
    //trial length
    {
        field: "trialLength",
        headerName: "Trial Term",
        minWidth: 130,
        width: 130,
        filterable: false,
    },
];
function getClassNameForRow(rowModel) {
    return rowModel.row.highlightedTrial ? "highlighted-trial" : "";
}
const TrialControl = ({ filterModels, onRowClick, layoutType, onTrialsLoaded, }) => {
    const navigate = useNavigate();
    const [trials, setTrials] = useState([]);
    const [filteredTrials, setFilteredTrials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 15,
    });
    const isMobile = useMediaQuery("(max-width:600px)");
    const columns = useMemo(() => {
        const trial = trialColumns.find((column) => column.field === "name");
        if (trial != null) {
            trial.width = isMobile ? 185 : 300;
        }
        return [...trialColumns];
    }, [isMobile]);
    //similar to Results.tsx, this useEffect will fetch the trials from the indexedDB
    useEffect(() => {
        const fetchTrials = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const apiResponse = yield getTrials();
                //unauthorised
                if (apiResponse.status === 401) {
                    navigate("/not-authorized");
                    return;
                }
                if (!Array.isArray(apiResponse.data))
                    apiResponse.data = [];
                //convert varieties to an array of objects
                let distinctSpeciesInTrials = [];
                apiResponse.data.forEach((trial) => {
                    var _a, _b;
                    trial.trialVarieties = JSON.parse(trial.varieties);
                    if (trial.trialData) {
                        trial.trialData = JSON.parse(trial.trialData);
                    }
                    //add to the distinct species array if it is not already there
                    if (trial.speciesName && trial.speciesName !== null) {
                        if (!distinctSpeciesInTrials.includes((_a = trial.speciesName) !== null && _a !== void 0 ? _a : "")) {
                            distinctSpeciesInTrials.push((_b = trial.speciesName) !== null && _b !== void 0 ? _b : "");
                        }
                        //sort the array alphabetically
                        distinctSpeciesInTrials.sort();
                    }
                });
                if (onTrialsLoaded) {
                    onTrialsLoaded(distinctSpeciesInTrials);
                }
                setTrials(apiResponse.data.sort((a, b) => {
                    // sort on highlighted trial first, then trial year
                    if (a.highlightedTrial && !b.highlightedTrial)
                        return -1;
                    if (!a.highlightedTrial && b.highlightedTrial)
                        return 1;
                    if (a.trialYear && b.trialYear)
                        return b.trialYear - a.trialYear;
                    return 0;
                }));
            }
            catch (error) {
                console.error(error);
            }
            finally {
                setLoading(false);
            }
        });
        fetchTrials();
    }, [navigate]);
    // Filter trials based on the filter models
    useEffect(() => {
        if (filterModels && filterModels.length === 0) {
            setFilteredTrials(trials);
            return;
        }
        let filtered = trials;
        let currentFilters = [];
        if (filterModels)
            currentFilters = filterModels.filter((filter) => filter.checked);
        //================================================================================================
        //                              AND CONDITIONS (Must exist in all conditions)
        //================================================================================================
        // this section filters the trials based on the filter models further for the between operators
        // it uses the every function to check if all the conditions are true instead of the some function
        // We should only do this IF there are between operators in the filter models
        // If there are no between operators, we can skip this section
        if (currentFilters &&
            currentFilters.some(
            //only do this if there are between operators in the filter models and the filter is checked
            (filter) => filter.checked &&
                (filter.operator === "between" || filter.operator === "="))) {
            filtered = filtered.filter((trial) => {
                if (!currentFilters)
                    return true;
                //all of these conditions must be true (These are for the between filters passed in)
                return currentFilters.every((filter) => {
                    var _a;
                    const trialValue = trial[filter.name];
                    if (!trialValue)
                        return false;
                    if (filter.operator === "between" && typeof trialValue === 'number') {
                        const [min, max] = filter.value
                            .split("<->")
                            .map((x) => parseFloat(x));
                        // For optional filters, if the min and max are 0, then we return true as it is optional
                        // check if min and max are number if not set them to 0
                        if (filter.name === "annualRainfall" &&
                            (isNaN(min) ? 0 : min) === 0 &&
                            (isNaN(max) ? 0 : max) === 0 &&
                            // Irrigated value
                            ((_a = trial["irrigated"]) !== null && _a !== void 0 ? _a : false) === true) {
                            return true;
                        }
                        return trialValue >= min && trialValue <= max;
                    }
                    if (filter.operator === "=") {
                        //For irrigated if the filter is present then it is checked and we included it in the filter
                        return (trialValue.toString().toLowerCase() ===
                            filter.value.toString().toLowerCase());
                    }
                    return true;
                });
            });
        }
        //================================================================================================
        //================================================================================================
        //                              OR CONDITIONS (Any of these conditions can be true)
        //================================================================================================
        // only do this if there are like operators in the filter models
        // if there are no like operators, we can skip this section
        if (currentFilters &&
            currentFilters.some((filter) => filter.checked && filter.operator === "like")) {
            filtered = filtered.filter((trial) => {
                if (!currentFilters)
                    return true;
                //any of these conditions can be true (These are for the like filters passed in)
                return currentFilters.some((filter) => {
                    var _a;
                    //if the filter is checked and the value is empty, return true
                    if (filter.returnTrueOnEmpty && filter.checked && filter.value === "")
                        return true;
                    // Need to take into account the varieties[] array as well as the other fields
                    if (filter.name === "trialVarieties" && trial.trialVarieties) {
                        return trial.trialVarieties.some((variety) => variety.varietyName
                            .toLowerCase()
                            .includes(filter.value.toLowerCase()));
                    }
                    const trialValue = (_a = trial[filter.name]) === null || _a === void 0 ? void 0 : _a.toString().toLowerCase();
                    if (!trialValue)
                        return false;
                    if (filter.operator === "like") {
                        return filter.value
                            .split(" ")
                            .some((term) => trialValue.includes(term.toLowerCase()));
                    }
                    return false;
                });
            });
        }
        //================================================================================================
        // set the state now that the filtering is done
        setFilteredTrials(filtered);
    }, [filterModels, trials]);
    //rowclick event to passthrough for the generic table
    const handleRowClick = (trialID) => {
        onRowClick && onRowClick(); //handles the reset of the table by changing the state on the parent
        navigate(`/results/${trialID}`);
    };
    const renderCell = (params) => (React.createElement(Grid, { container: true, spacing: 1 },
        React.createElement(Grid, { item: true, xs: 12 },
            React.createElement(Typography, { sx: {
                    //wrap the text
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                } },
                " ",
                params.row.name,
                " (",
                params.row.trialYear,
                ")"))));
    const mobileColumns = [
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            renderCell,
        },
    ];
    const CustomNoRowsOverlay = () => {
        return (React.createElement(GridOverlay, null,
            React.createElement(Typography, { variant: "body2", color: "textSecondary" }, "No trials currently available")));
    };
    //get screen width and set Container width to 100% if it is a mobile device
    // otherwise set the width to screen width - 200px
    let width = window.innerWidth;
    //on window resize, update the width
    window.onresize = () => {
        const newWidth = window.innerWidth;
        if (newWidth !== width) {
            width = newWidth;
        }
    };
    return (React.createElement(Box, { sx: {
            overflowX: "auto",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minHeight: "200px",
            maxHeight: "100vh", // per mui recommendation https://mui.com/x/react-data-grid/layout/#flex-parent-container
        } }, loading ? (React.createElement(LoadingBackdrop, { loading: loading })) : layoutType === "list" ? (React.createElement(DataGrid, { className: "trial-mobile-grid", rows: filteredTrials, columns: mobileColumns, pagination: true, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, pageSizeOptions: [15, 20, 50], onRowClick: (row) => handleRowClick(row.id), checkboxSelection: false, disableRowSelectionOnClick: true, disableVirtualization // not needed here, and causes blank rows
        : true, sx: {
            // marginTop: "20px",
            "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#005533",
                color: "#ffffff",
                textAlign: "center",
                fontWeight: "bold",
                whiteSpace: "normal",
                wordWrap: "break-word",
                overflowWrap: "break-word",
            },
            "& .MuiTablePagination-root": {
                overflow: "hidden",
            },
        }, slots: {
            noRowsOverlay: CustomNoRowsOverlay,
        } })) : (React.createElement(DataGrid, { className: "trial-grid", sx: {
            // marginTop: "20px",
            "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#005533",
                color: "#ffffff",
                textAlign: "center",
                fontWeight: "bold",
            },
            "& .MuiDataGrid-filler": {
                backgroundColor: "transparent",
            },
            "& .MuiDataGrid-container--top [role=row]": {
                backgroundColor: "#005533",
            },
            "& .MuiDataGrid-row:hover": {
                cursor: "pointer",
            },
            "& .MuiTablePagination-root": {
                overflow: "hidden",
            },
        }, rows: filteredTrials, columns: columns, disableVirtualization // not needed here, and causes blank rows
        : true, pagination: true, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, pageSizeOptions: [15, 20, 50], onRowClick: (row) => handleRowClick(row.id), checkboxSelection: false, disableRowSelectionOnClick: true, getRowClassName: (row) => getClassNameForRow(row), slots: {
            noRowsOverlay: CustomNoRowsOverlay,
        } }))));
};
export default TrialControl;
